import {
  FILE_TABLE_ACTION_DOWNLOAD,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { onPremHttpRequest } from "core/model/careseekers";
import { base64ToBytes } from "core/model/crypto";
import { createAndClickLink, downloadFile } from "core/model/files";
import { decryptFromSession } from "core/seald";
import { FileForm, FileHISStateful, Filev2, QueryProgress } from "core/types";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { useFetchFormDataAndGeneratePdf } from "./useFetchFormDataAndGeneratePdf";
import { FileFeatureData } from "./useFilesFeature";
import { isFileForm } from "./utils";

export async function handleFileHISDownload({
  externalId,
  file,
  on_premise_authorization_token,
  on_premise_domain_ssl,
  setAction,
  setProgress,
  toast,
  translations,
}: {
  externalId: string;
  file: FileHISStateful;
  on_premise_authorization_token: string | undefined;
  on_premise_domain_ssl: string | undefined;
  setAction: SetAction<FileFeatureData>;
  setProgress: Dispatch<SetStateAction<QueryProgress>>;
  toast: ReturnType<typeof useToast>;
  translations: Translations;
}) {
  try {
    const response = await onPremHttpRequest({
      method: "GET",
      on_premise_authorization_token,
      on_premise_domain_ssl,
      path: `patients/${externalId}/documents/${file.document_id}`,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch file: ${response.status} ${response.statusText}`,
      );
    }

    const blob = await response.blob();
    createAndClickLink({ blob, fileName: file.document_file_name });

    setProgress(QUERY_PROGRESS_SUCCEED);
  } catch (err) {
    console.error(err);
    setProgress(QUERY_PROGRESS_FAILED);
    toast({
      color: "danger",
      message: translations.fileSection.toastError,
    });
  } finally {
    setAction(undefined);
  }
}

export function useDownloadFile({
  action,
  externalId,
  setAction,
  setProgress,
}: {
  action: GeneralListAction<FileHISStateful | Filev2 | FileForm> | undefined;
  externalId: string;
  setAction: SetAction<FileFeatureData>;
  setProgress: Dispatch<SetStateAction<QueryProgress>>;
}) {
  const toast = useToast();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();
  const translations = useTranslations();

  const fetchFormDataAndGeneratePdf = useFetchFormDataAndGeneratePdf({
    externalId,
  });

  async function handleFileDownload({ file }: { file: Filev2 }) {
    setProgress(QUERY_PROGRESS_PENDING);

    try {
      if (!file.link || !file.file_name?.seald_content || !file.file_type) {
        throw new Error(
          "[handleFileDownload] link, encrypted file_name and file type must be defined!",
        );
      }

      const response = await fetch(file.link, {
        headers: { Accept: "*/*" },
      });
      const text = await response.text();

      if (!text) {
        throw new Error("[handleFileDownload] Couldn't fetch encrypted file!");
      }

      const decryptedBase64File = await decryptFromSession({
        context: "file",
        encryptedMessage: text,
      });

      if (!decryptedBase64File) {
        throw new Error("[handleFileDownload] Couldn't decrypt file content!");
      }

      const fileName = await decryptFromSession({
        context: `[handleFileDownload] - file_name`,
        encryptedMessage: file.file_name.seald_content,
      });

      if (!fileName) {
        throw new Error("[handleFileDownload] Couldn't decrypt file name!");
      }

      downloadFile({
        content: base64ToBytes(decryptedBase64File),
        fileName,
        type: file.file_type,
      });

      setProgress(QUERY_PROGRESS_SUCCEED);
    } catch (err) {
      console.error(err);
      setProgress(QUERY_PROGRESS_FAILED);
      toast({
        color: "danger",
        message: translations.fileSection.toastError,
      });
    } finally {
      setAction(undefined);
    }
  }

  useEffect(() => {
    const handleAction = async () => {
      if (action?.type === FILE_TABLE_ACTION_DOWNLOAD) {
        if (isFileHISStateful(action.data)) {
          await handleFileHISDownload({
            externalId,
            file: action.data,
            on_premise_authorization_token,
            on_premise_domain_ssl,
            setAction,
            setProgress,
            toast,
            translations,
          });
          return;
        }

        if (isFileForm(action.data)) {
          setProgress(QUERY_PROGRESS_PENDING);

          try {
            const { fileName, pdfBytes } = await fetchFormDataAndGeneratePdf({
              file: action.data,
            });

            downloadFile({
              content: pdfBytes,
              fileName,
              type: "application/pdf",
            });
          } catch (err) {
            console.error(err);
            setProgress(QUERY_PROGRESS_FAILED);
            toast({
              color: "danger",
              message: translations.fileSection.toastError,
            });
          } finally {
            setAction(undefined);
          }
          return;
        }

        handleFileDownload({ file: action.data });
      }
    };

    handleAction();
  }, [action?.type]);
}
